// src/components/GoalsPanel.js
//
// The races, as rows: one long-term A goal and any number of B/C races on
// the way, each a row that opens the race in a sheet (GoalEditSheet). Lives
// inside the Goal & plan sheet. Every change is saved to the server at once,
// but the plan is only rebuilt when the athlete asks for it: a saved or
// removed race marks the plan as out of date and the Rebuild button carries
// it. Editing a B race never drops the athlete into the plan wizard.

import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Plus, ChevronRight } from "lucide-react";
import { api } from "../api";
import GoalEditSheet, { fmtHms, timeLabel, distanceLabel, fmtDateShort, weeksUntil, raceVar, terrainLabel } from "./GoalEditSheet";

export { fmtHms };

export default function GoalsPanel({ goals, onGoalsChange, onRebuild, rebuilding, planWarnings, planGoalIds, planGoals, onOpenWizard }) {
  const [editing, setEditing] = useState(null); // null | 'new' | goal
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState("");

  const planned = useMemo(() => (goals || []).filter((g) => g.status === "planned" && g.raceDate).sort((a, b) => (a.raceDate < b.raceDate ? -1 : 1)), [goals]);
  const ambitions = useMemo(() => (goals || []).filter((g) => g.status === "someday"), [goals]);
  const hasA = planned.some((g) => g.priority === "A");
  const planIds = new Set(planGoalIds || []);
  const hasPlan = planIds.size > 0;
  const staleGoals = planned.filter((g) => !planIds.has(g.id));
  /* What changed since the plan was built, read against the goal snapshot
     the plan stores (plan.goals): a race in the plan that was edited, and a
     race the plan is built around that has since been removed. New races
     are staleGoals above. */
  const snapById = useMemo(() => {
    const m = new Map();
    for (const g of planGoals || []) if (g && g.id != null) m.set(g.id, g);
    return m;
  }, [planGoals]);
  const editedGoals = planned.filter((g) => {
    const p = planIds.has(g.id) ? snapById.get(g.id) : null;
    if (!p) return false;
    return (p.raceDate || "") !== (g.raceDate || "")
      || Math.abs((Number(p.distanceKm) || 0) - (Number(g.distanceKm) || 0)) > 0.01
      || Math.round(Number(p.targetFinishSec) || 0) !== Math.round(Number(g.targetFinishSec) || 0)
      || (p.priority || "") !== (g.priority || "");
  });
  const plannedIds = new Set(planned.map((g) => g.id));
  const removedCount = [...planIds].filter((id) => id != null && snapById.has(id) && !plannedIds.has(id)).length;
  const changedCount = (hasPlan ? staleGoals.length : 0) + editedGoals.length + removedCount;
  const warnById = useMemo(() => {
    const m = new Map();
    for (const w of planWarnings || []) m.set(w.goalId, w);
    return m;
  }, [planWarnings]);

  /* The two fixes the long-run warning names, as one tap each. Both save the
     goal and rebuild the plan (which runs the readiness gate as usual), so
     the warning is re-evaluated against the rebuilt plan rather than being
     advice the athlete has to carry out by hand. */
  const applyGoalFix = async (g, patch, label) => {
    setBusyId(g.id);
    setErr("");
    try {
      const r = await api.post("/api/goals", Object.assign({}, g, patch));
      onGoalsChange(r.goals);
      if (onRebuild) await onRebuild();
    } catch (e) {
      setErr(`Could not ${label}: ${e?.message || "try again"}`);
    } finally {
      setBusyId(null);
    }
  };
  const moveRaceLater = (g, date) => applyGoalFix(g, { raceDate: date }, "move the race");
  const dropTarget = (g) => applyGoalFix(g, { targetFinishSec: null, targetFinish: null }, "drop the target");

  /* A saved or removed race: the list updates at once and the sheet closes.
     The plan is not touched; the foot below says it is out of date. */
  const afterChange = (list) => {
    onGoalsChange(list);
    setEditing(null);
  };

  const raceRow = (g, kind) => {
    const warn = warnById.get(g.id);
    const km = Number(g.distanceKm) || 0;
    const withSec = km > 0 && km <= 42.195;
    const wks = kind === "someday" ? null : weeksUntil(g.raceDate);
    const notInPlan = kind !== "someday" && hasPlan && !planIds.has(g.id);
    const changedInPlan = kind !== "someday" && editedGoals.includes(g);
    const meta = kind === "someday"
      ? ["No date yet", `${distanceLabel(km)}`, terrainLabel(g.terrain)]
      : [fmtDateShort(g.raceDate), wks != null ? `${wks} wks` : null, terrainLabel(g.terrain)];
    return (
      <div key={g.id} className={"gpRace gpRace--" + (kind === "someday" ? "someday" : g.priority) + (busyId === g.id ? " is-busy" : "")} style={{ "--type": raceVar(km) }}>
        <button type="button" className="gpRace__hit" onClick={() => setEditing(g)} disabled={busyId === g.id} aria-label={`${g.name || distanceLabel(km)}, ${meta.filter(Boolean).join(", ")}. Edit.`}>
          <span className="gpRace__spine" aria-hidden="true" />
          <span className={"gpRace__prio" + (kind === "someday" ? " gpRace__prio--someday" : "")} aria-hidden="true">{kind === "someday" ? "∞" : g.priority}</span>
          <span className="gpRace__text">
            <span className="gpRace__name">{g.name || distanceLabel(km)}{g.name && g.name !== distanceLabel(km) ? <span className="gpRace__dist"> · {distanceLabel(km)}</span> : null}</span>
            <span className="gpRace__meta">
              {meta.filter(Boolean).map((m, i) => <React.Fragment key={i}>{i > 0 ? <span className="gpRace__sep" /> : null}{m}</React.Fragment>)}
              {g.dateSuggested ? <><span className="gpRace__sep" /><span className="gpRace__sugg">suggested</span></> : null}
              {notInPlan ? <><span className="gpRace__sep" /><span className="gpRace__stale">not in plan yet</span></> : null}
              {changedInPlan ? <><span className="gpRace__sep" /><span className="gpRace__stale">changed</span></> : null}
            </span>
          </span>
          <span className="gpRace__right">
            <span className="gpRace__target">{g.targetFinishSec ? <>{timeLabel(Math.floor(g.targetFinishSec), withSec)}<span className="gpRace__u">h</span></> : <span className="gpRace__finish">Finish</span>}</span>
            <ChevronRight size={16} className="gpRace__chev" aria-hidden="true" />
          </span>
        </button>
        {warn ? (
          <div className="gpRace__warnWrap">
            <div className="gpRace__warn">{warn.message}</div>
            {(warn.suggestedRaceDate || warn.canDropTarget) ? (
              <div className="gpRace__fixes">
                {warn.suggestedRaceDate ? (
                  <button type="button" className="gpFix" disabled={busyId === g.id || rebuilding} onClick={() => moveRaceLater(g, warn.suggestedRaceDate)}>
                    Move to {fmtDateShort(warn.suggestedRaceDate)}
                  </button>
                ) : null}
                {warn.canDropTarget ? (
                  <button type="button" className="gpFix" disabled={busyId === g.id || rebuilding} onClick={() => dropTarget(g)}>
                    Race it as a hard effort
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="gpRaces">
      {err ? <div role="alert" className="agError gpErr">{err}</div> : null}
      {planned.length === 0 ? (
        <div className="gpEmpty">
          Add a race with a date: 5K, 10K, half, marathon or an ultra. The latest one anchors the plan; anything before it is embedded with a mini-taper and recovery.
        </div>
      ) : null}

      {planned.map((g) => raceRow(g, "planned"))}

      <button type="button" className="gpAdd" onClick={() => (!planned.length && onOpenWizard ? onOpenWizard() : setEditing("new"))}>
        <span className="gpAdd__icon" aria-hidden="true"><Plus size={14} /></span>
        <span className="gpAdd__label">{planned.length ? "Add a race" : "Add your first goal"}</span>
      </button>

      {ambitions.length > 0 ? (
        <div className="gpAmbitions">
          <div className="gpSub">Ambitions</div>
          {ambitions.map((g) => raceRow(g, "someday"))}
        </div>
      ) : null}

      {planned.length > 0 ? (
        <div className="gpFoot">
          <button type="button" className="sessionSheetBtn sessionSheetBtn--primary gpRebuild" onClick={onRebuild} disabled={rebuilding}>
            {rebuilding ? "Rebuilding…" : !hasPlan ? "Build plan from goals" : "Rebuild plan"}
          </button>
          {changedCount > 0 && hasPlan ? (
            <div className="gpFoot__note">{changedCount} {changedCount === 1 ? "race" : "races"} changed since the plan was built. Your current plan stays until you rebuild.</div>
          ) : null}
        </div>
      ) : null}

      {editing ? createPortal(
        <GoalEditSheet
          goal={editing === "new" ? null : editing}
          hasA={hasA}
          onClose={() => setEditing(null)}
          onSaved={afterChange}
          onRemoved={afterChange}
        />,
        document.body
      ) : null}
    </div>
  );
}
